import type { Incident } from '../types/incident';
import {
  KanbanBoard,
  KanbanBoardAccess,
  KanbanBoardAccessOptions,
  KanbanBoardPermissions,
  KanbanBoardWithDetails,
  KanbanFullBoard,
  KanbanList,
  KanbanSwimlane,
  KanbanCard,
  KanbanCardFull,
  KanbanCardSummary,
  KanbanAssignee,
  KanbanLabel,
  KanbanChecklist,
  KanbanChecklistItem,
  KanbanComment,
  KanbanAttachment,
  KanbanWatcher,
  KanbanCardLink,
  KanbanCardHistory,
  KanbanCadence,
  KanbanUpcomingCadence,
  KanbanBoardSetting,
  KanbanActivityLogEntry,
  KanbanLeadCycleTimeMetrics,
  KanbanThroughputMetrics,
  KanbanWIPAgingEntry,
  KanbanBottleneckEntry,
  KanbanCFDEntry,
  KanbanMetricsSummary,
  KanbanStats,
  KanbanPaginatedResponse,
  CardCountByList,
  CreateBoardRequest,
  UpdateBoardRequest,
  CreateListRequest,
  UpdateListRequest,
  CreateSwimlaneRequest,
  UpdateSwimlaneRequest,
  CreateCardRequest,
  UpdateCardRequest,
  MoveCardRequest,
  BatchMoveRequest,
  BlockCardRequest,
  CreateLabelRequest,
  UpdateLabelRequest,
  CreateChecklistRequest,
  UpdateChecklistRequest,
  CreateChecklistItemRequest,
  UpdateChecklistItemRequest,
  CreateCommentRequest,
  UpdateCommentRequest,
  CreateCardLinkRequest,
  CreateCadenceRequest,
  UpdateCadenceRequest,
  SetBoardSettingRequest,
  ListBoardsParams,
  ListCardsParams,
} from '../types/kanban';
import { normalizeKanbanTeamsResponse } from '../utils/kanbanPermissions';
import { coerceKanbanEntityId } from '../utils/kanbanEntityIds';

/** Kanban REST + WebSocket base (same origin as WS upgrade per `temporary/kanban/KANBAN_WEBSOCKET.md`). */
export const KANBAN_API_BASE_URL = 'https://spy.playcdu.co/api/notes';

/** First non-array argument wins; backend payloads vary by endpoint. */
function firstArray(...candidates: unknown[]): unknown[] {
  for (const c of candidates) {
    if (Array.isArray(c)) return c;
  }
  return [];
}

function coercePaginatedPayload<T>(data: unknown): KanbanPaginatedResponse<T> {
  if (Array.isArray(data)) {
    const rows = data;
    return { page: 1, limit: rows.length, total: rows.length, results: rows as T[] };
  }
  if (!data || typeof data !== 'object') {
    return { page: 1, limit: 0, total: 0, results: [] };
  }
  const o = data as Record<string, unknown>;
  const rows = firstArray(
    o.results,
    o.boards,
    o.cards,
    o.data,
    o.items,
    o.entries,
    o.activity,
    o.events,
    o.log,
    o.recent
  );
  return {
    page: typeof o.page === 'number' ? o.page : 1,
    limit: typeof o.limit === 'number' ? o.limit : rows.length,
    total: typeof o.total === 'number' ? o.total : rows.length,
    results: rows as T[],
  };
}

function coerceSearchBoardPayload(data: unknown): { results: KanbanBoard[] } {
  const p = coercePaginatedPayload<KanbanBoard>(data);
  return { results: p.results };
}

function coerceRecentActivityPayload(data: unknown): KanbanActivityLogEntry[] {
  if (Array.isArray(data)) return data as KanbanActivityLogEntry[];
  if (!data || typeof data !== 'object') return [];
  const o = data as Record<string, unknown>;
  const rows = firstArray(
    o.results,
    o.entries,
    o.activity,
    o.data,
    o.items,
    o.events,
    o.log,
    o.recent
  );
  return rows as KanbanActivityLogEntry[];
}

/** Full-board and list-settings endpoints sometimes return `{}` instead of a row array. */
export function coerceKanbanBoardSettings(raw: unknown): KanbanBoardSetting[] {
  if (Array.isArray(raw)) {
    return raw.filter(
      (row): row is KanbanBoardSetting =>
        !!row &&
        typeof row === 'object' &&
        typeof (row as KanbanBoardSetting).setting_key === 'string' &&
        typeof (row as KanbanBoardSetting).setting_value === 'string',
    );
  }
  if (raw && typeof raw === 'object') {
    return Object.entries(raw as Record<string, unknown>).map(([setting_key, setting_value]) => ({
      board_id: '',
      setting_key,
      setting_value: setting_value == null ? '' : String(setting_value),
      updated_at: 0,
      updated_by: '',
    }));
  }
  return [];
}

export function emptyKanbanBoardPlaceholder(boardId: string): KanbanBoard {
  return {
    id: boardId,
    team: '',
    title: '',
    description: null,
    is_default: 0,
    background_color: null,
    is_archived: 0,
    created_at: 0,
    created_by: '',
    updated_at: null,
    updated_by: null,
  };
}

/** Unix ms from API (number, numeric string, or ISO date string). */
function parseEpochMs(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    if (/^\d+$/.test(value)) {
      const n = Number(value);
      return Number.isFinite(n) ? n : null;
    }
    const d = Date.parse(value);
    return Number.isNaN(d) ? null : d;
  }
  return null;
}

function asRecordLoose(v: unknown): Record<string, unknown> | null {
  return v && typeof v === 'object' ? (v as Record<string, unknown>) : null;
}

/**
 * Find the board-shaped object inside common API envelopes (`{ board }`, `{ data }`, `{ result }`, …).
 */
function extractBoardRecordFromEnvelope(raw: unknown): Record<string, unknown> | null {
  if (!raw || typeof raw !== 'object') return null;
  const root = raw as Record<string, unknown>;

  const tryNode = (node: unknown): Record<string, unknown> | null => {
    const r = asRecordLoose(node);
    if (!r) return null;
    const id = coerceKanbanEntityId(r.id);
    if (id == null) return null;
    return { ...r, id };
  };

  const nestedBoard = (container: unknown): Record<string, unknown> | null => {
    const c = asRecordLoose(container);
    if (!c) return null;
    return tryNode(c.board) ?? tryNode(c);
  };

  return (
    tryNode(root.board) ??
    nestedBoard(root.data) ??
    nestedBoard(root.result) ??
    nestedBoard(root.payload) ??
    tryNode(root)
  );
}

function coerceKanbanBoardFromRecord(r: Record<string, unknown>, fallbackId: string): KanbanBoard {
  const id = coerceKanbanEntityId(r.id);
  if (id == null) return emptyKanbanBoardPlaceholder(fallbackId);
  const updatedMs = parseEpochMs(r.updated_at);
  return {
    id,
    team: typeof r.team === 'string' ? r.team : '',
    title: typeof r.title === 'string' ? r.title : '',
    description: r.description == null ? null : String(r.description),
    is_default: typeof r.is_default === 'number' ? r.is_default : 0,
    background_color: r.background_color == null ? null : String(r.background_color),
    is_archived: typeof r.is_archived === 'number' ? r.is_archived : 0,
    created_at: parseEpochMs(r.created_at) ?? 0,
    created_by: typeof r.created_by === 'string' ? r.created_by : '',
    updated_at: updatedMs,
    updated_by: r.updated_by == null ? null : String(r.updated_by),
    ...(r.permissions && typeof r.permissions === 'object' ? {
      permissions: {
        read: (r.permissions as KanbanBoardPermissions).read === true,
        write: (r.permissions as KanbanBoardPermissions).write === true,
        manage: (r.permissions as KanbanBoardPermissions).manage === true,
        manage_access: (r.permissions as KanbanBoardPermissions).manage_access === true,
      },
    } : {}),
  };
}

/** POST /boards may return `{ board }` or partial timestamps; normalize for the UI. */
export function normalizeKanbanBoardFromApiResponse(raw: unknown): KanbanBoard {
  if (!raw || typeof raw !== 'object') {
    return emptyKanbanBoardPlaceholder('');
  }
  const inner = extractBoardRecordFromEnvelope(raw);
  if (!inner) {
    return emptyKanbanBoardPlaceholder('');
  }
  const id = coerceKanbanEntityId(inner.id) ?? '';
  return coerceKanbanBoardFromRecord(inner, id);
}

/** API sometimes returns `comments_count` / plural counts; UI uses `comment_count`. */
function normalizeKanbanCardSummaryRow(row: unknown): KanbanCardSummary | null {
  if (!row || typeof row !== 'object') return null;
  const c = row as Record<string, unknown>;
  if (typeof c.id !== 'string') return null;
  const base = c as unknown as KanbanCardSummary;
  const assignees = Array.isArray(c.assignees) ? (c.assignees as KanbanCardSummary['assignees']) : [];
  const labelsOnCard = Array.isArray(c.labels) ? (c.labels as KanbanCardSummary['labels']) : [];
  const checklist_progress =
    c.checklist_progress &&
    typeof c.checklist_progress === 'object' &&
    c.checklist_progress !== null &&
    typeof (c.checklist_progress as { total?: unknown }).total === 'number' &&
    typeof (c.checklist_progress as { checked?: unknown }).checked === 'number'
      ? (c.checklist_progress as KanbanCardSummary['checklist_progress'])
      : { total: 0, checked: 0 };
  const num = (a: unknown, b: unknown): number => {
    if (typeof a === 'number') return a;
    if (typeof b === 'number') return b;
    return 0;
  };
  const linksFromRow = Array.isArray(c.links) ? (c.links as KanbanCardLink[]) : [];
  const links = linksFromRow.length > 0 ? linksFromRow : undefined;

  return {
    ...base,
    labels: labelsOnCard,
    assignees,
    checklist_progress,
    comment_count: num(c.comment_count, c.comments_count),
    attachment_count: num(c.attachment_count, c.attachments_count),
    watcher_count: num(c.watcher_count, c.watchers_count),
    link_count: num(c.link_count, c.links_count),
    ...(links ? { links } : {}),
  };
}

/** Derive board card-summary fields from a full card (move API returns base card only). */
export function cardFullToBoardSummaryPatch(full: KanbanCardFull): Pick<
  KanbanCardSummary,
  | 'labels'
  | 'assignees'
  | 'checklist_progress'
  | 'comment_count'
  | 'attachment_count'
  | 'watcher_count'
  | 'link_count'
> {
  const checklists = Array.isArray(full.checklists) ? full.checklists : [];
  let checked = 0;
  let total = 0;
  for (const cl of checklists) {
    const items = Array.isArray(cl.items) ? cl.items : [];
    total += items.length;
    checked += items.filter((i) => i.is_checked === 1).length;
  }
  const labels = (full.labels ?? []).map((l) => ({ id: l.id, name: l.name, color: l.color }));
  const assignees = (full.assignees ?? []).map((a) => ({ user_uuid: a.user_uuid, assigned_at: a.assigned_at }));
  return {
    labels,
    assignees,
    checklist_progress: { total, checked },
    comment_count: full.comments?.length ?? 0,
    attachment_count: full.attachments?.length ?? 0,
    watcher_count: full.watchers?.length ?? 0,
    link_count: full.links?.length ?? 0,
  };
}

/** Full card → board `cards[]` row (base fields + summary counts/labels/assignees). */
export function cardFullToBoardCardSummary(full: KanbanCardFull): KanbanCardSummary {
  const {
    labels: _labels,
    assignees: _assignees,
    checklists: _checklists,
    comments: _comments,
    attachments: _attachments,
    watchers: _watchers,
    links: _links,
    ...base
  } = full;
  const links = full.links?.length ? full.links : undefined;

  return {
    ...(base as KanbanCard),
    ...cardFullToBoardSummaryPatch(full),
    ...(links ? { links } : {}),
  };
}

function listsWithoutEmbeddedCards(listsUnknown: unknown[]): KanbanList[] {
  return listsUnknown.map((item) => {
    if (!item || typeof item !== 'object') return item as KanbanList;
    const L = item as Record<string, unknown>;
    const { cards: _cards, card_count: _cc, ...rest } = L;
    return rest as unknown as KanbanList;
  });
}

function cardsFromNestedListPayload(listsUnknown: unknown[]): KanbanCardSummary[] {
  const out: KanbanCardSummary[] = [];
  for (const item of listsUnknown) {
    if (!item || typeof item !== 'object') continue;
    const embedded = (item as Record<string, unknown>).cards;
    if (!Array.isArray(embedded)) continue;
    for (const row of embedded) {
      const card = normalizeKanbanCardSummaryRow(row);
      if (card) out.push(card);
    }
  }
  return out;
}

/**
 * `/full` may return either `{ board, cards, lists, ... }` or a **flat board** document
 * (`id`, `title`, `lists[].cards`, …) from KV/cache. Accept both so the board view always hydrates.
 */
function coerceKanbanFullBoard(boardId: string, raw: unknown): KanbanFullBoard {
  if (!raw || typeof raw !== 'object') {
    const board = emptyKanbanBoardPlaceholder(boardId);
    return {
      board,
      lists: [],
      swimlanes: [],
      labels: [],
      settings: [],
      cards: [],
    };
  }
  const o = raw as Record<string, unknown>;
  const listsRaw = Array.isArray(o.lists) ? o.lists : [];
  const swimlanes = Array.isArray(o.swimlanes) ? (o.swimlanes as KanbanSwimlane[]) : [];
  const labels = Array.isArray(o.labels) ? (o.labels as KanbanLabel[]) : [];

  const nestedCards = cardsFromNestedListPayload(listsRaw);
  const hasNestedCards = nestedCards.length > 0;
  const topLevelCardsRaw = Array.isArray(o.cards) ? o.cards : [];
  const topLevelCards = topLevelCardsRaw
    .map(normalizeKanbanCardSummaryRow)
    .filter((c): c is KanbanCardSummary => c != null);

  let board: KanbanBoard;
  if (o.board && typeof o.board === 'object' && o.board !== null) {
    board = o.board as KanbanBoard;
  } else if (typeof o.id === 'string' && (hasNestedCards || listsRaw.length > 0 || typeof o.title === 'string')) {
    board = coerceKanbanBoardFromRecord(o, boardId);
  } else {
    board = emptyKanbanBoardPlaceholder(boardId);
  }

  const lists = listsWithoutEmbeddedCards(listsRaw) as KanbanList[];

  const cardById = new Map<string, KanbanCardSummary>();
  for (const c of nestedCards) cardById.set(c.id, c);
  for (const c of topLevelCards) cardById.set(c.id, c);
  const cards = Array.from(cardById.values());

  return {
    board,
    lists,
    swimlanes,
    labels,
    settings: coerceKanbanBoardSettings(o.settings),
    cards,
  };
}

const KANBAN_CARD_TYPES_SET = new Set<string>([
  'bug',
  'feature',
  'task',
  'story',
  'improvement',
  'epic',
  'subtask',
  'other',
]);

function relationArrayFromCardPayload<T>(
  root: Record<string, unknown>,
  inner: Record<string, unknown>,
  key: string,
): T[] {
  if (Object.prototype.hasOwnProperty.call(root, key)) {
    const v = root[key];
    return Array.isArray(v) ? (v as T[]) : [];
  }
  if (Object.prototype.hasOwnProperty.call(inner, key)) {
    const v = inner[key];
    return Array.isArray(v) ? (v as T[]) : [];
  }
  return [];
}

function coerceKanbanCardFullResponse(raw: unknown): KanbanCardFull {
  if (!raw || typeof raw !== 'object') {
    throw new Error('Invalid card response');
  }
  const o = raw as Record<string, unknown>;

  const inner =
    o.card && typeof o.card === 'object' && o.card !== null ? (o.card as Record<string, unknown>) : o;

  const rawType = inner.card_type;
  const card_type =
    typeof rawType === 'string' && KANBAN_CARD_TYPES_SET.has(rawType)
      ? (rawType as KanbanCard['card_type'])
      : 'task';

  const base = { ...inner, card_type } as unknown as KanbanCard;

  const labels = relationArrayFromCardPayload<KanbanLabel>(o, inner, 'labels');
  const assignees = relationArrayFromCardPayload<KanbanAssignee>(o, inner, 'assignees');
  const checklists = relationArrayFromCardPayload<KanbanChecklist>(o, inner, 'checklists').map((cl) => ({
    ...cl,
    items: Array.isArray(cl.items) ? cl.items : [],
  }));
  const comments = relationArrayFromCardPayload<KanbanComment>(o, inner, 'comments');
  const attachments = relationArrayFromCardPayload<KanbanAttachment>(o, inner, 'attachments');
  const watchers = relationArrayFromCardPayload<KanbanWatcher>(o, inner, 'watchers');
  const links = relationArrayFromCardPayload<KanbanCardLink>(o, inner, 'links');

  return {
    ...(base as KanbanCard),
    labels,
    assignees,
    checklists,
    comments,
    attachments,
    watchers,
    links,
  };
}

export class KanbanApiError extends Error {
  constructor(public readonly status: number, message: string) {
    super(message);
    this.name = 'KanbanApiError';
  }
}

class KanbanApiService {
  private async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const response = await fetch(`${KANBAN_API_BASE_URL}${endpoint}`, {
      ...options,
      credentials: 'include',
      // Cloudflare Access handles authentication via cookies
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new KanbanApiError(response.status, `API Error: ${response.status} ${response.statusText} - ${errorText}`);
    }

    return response.json();
  }

  private async requestRaw(endpoint: string, options?: RequestInit): Promise<Response> {
    const response = await fetch(`${KANBAN_API_BASE_URL}${endpoint}`, {
      ...options,
      credentials: 'include',
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new KanbanApiError(response.status, `API Error: ${response.status} ${response.statusText} - ${errorText}`);
    }

    return response;
  }

  private buildQueryString(params: Record<string, string | number | boolean | undefined | null>): string {
    const qs = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null && value !== '') {
        qs.append(key, String(value));
      }
    }
    const str = qs.toString();
    return str ? `?${str}` : '';
  }

  // ── 1. Board Endpoints (10) ─────────────────────────────────────────────

  /** 1.1 Create Board */
  async createBoard(data: CreateBoardRequest): Promise<KanbanBoard> {
    const raw = await this.request<unknown>('/kanban/boards', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return normalizeKanbanBoardFromApiResponse(raw);
  }

  /** 1.2 List Boards */
  async listBoards(params?: ListBoardsParams): Promise<KanbanPaginatedResponse<KanbanBoard>> {
    const qs = this.buildQueryString({
      team: params?.team,
      includeArchived: params?.includeArchived,
      page: params?.page,
      limit: params?.limit,
    });
    const raw = await this.request<unknown>(`/kanban/boards${qs}`);
    const p = coercePaginatedPayload<KanbanBoard>(raw);
    return {
      ...p,
      results: p.results.map((row) => normalizeKanbanBoardFromApiResponse(row)),
    };
  }

  /**
   * List every board the caller can access (no `team` query param).
   * Follows pagination until all rows are loaded (same cap as a single large `limit`).
   */
  async listAllBoards(params?: Omit<ListBoardsParams, 'team' | 'page'>): Promise<KanbanPaginatedResponse<KanbanBoard>> {
    const pageSize = params?.limit ?? 100;
    const merged = new Map<string, KanbanBoard>();
    let page = 1;
    const maxPages = 500;

    for (;;) {
      const batch = await this.listBoards({
        includeArchived: params?.includeArchived,
        page,
        limit: pageSize,
      });
      if (batch.results.length === 0) break;
      for (const row of batch.results) {
        merged.set(row.id, row);
      }
      if (batch.results.length < pageSize) break;
      page += 1;
      if (page > maxPages) break;
    }

    const results = Array.from(merged.values()).sort((a, b) => {
      const t = a.team.localeCompare(b.team);
      if (t !== 0) return t;
      return a.title.localeCompare(b.title);
    });

    return {
      page: 1,
      limit: results.length,
      total: results.length,
      results,
    };
  }

  /** 1.3 Get Board */
  async getBoard(boardId: string): Promise<KanbanBoardWithDetails> {
    return this.request(`/kanban/boards/${encodeURIComponent(boardId)}`);
  }

  /** 1.4 Get Full Board (KV Cached) */
  async getFullBoard(boardId: string): Promise<KanbanFullBoard> {
    const raw = await this.request<unknown>(`/kanban/boards/${encodeURIComponent(boardId)}/full`);
    return coerceKanbanFullBoard(boardId, raw);
  }

  /** 1.5 Update Board */
  async updateBoard(boardId: string, data: UpdateBoardRequest): Promise<KanbanBoard> {
    return this.request(`/kanban/boards/${encodeURIComponent(boardId)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  async getBoardAccess(boardId: string): Promise<KanbanBoardAccess> {
    return this.request(`/kanban/boards/${encodeURIComponent(boardId)}/access`);
  }

  async getBoardAccessOptions(boardId: string): Promise<KanbanBoardAccessOptions> {
    return this.request(`/kanban/boards/${encodeURIComponent(boardId)}/access-options`);
  }

  async setBoardAccess(boardId: string, data: Pick<KanbanBoardAccess, 'staff_teams' | 'staff_users'>): Promise<KanbanBoardAccess> {
    return this.request(`/kanban/boards/${encodeURIComponent(boardId)}/access`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  /** 1.6 Delete Board */
  async deleteBoard(boardId: string): Promise<{ ok: boolean; deleted: string }> {
    return this.request(`/kanban/boards/${encodeURIComponent(boardId)}`, {
      method: 'DELETE',
    });
  }

  /** 1.7 Archive / Unarchive Board */
  async archiveBoard(boardId: string, isArchived: boolean): Promise<KanbanBoard> {
    return this.request(`/kanban/boards/${encodeURIComponent(boardId)}/archive`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ is_archived: isArchived }),
    });
  }

  /** 1.8 Get or Create Default Board */
  async getOrCreateDefaultBoard(team: string): Promise<KanbanBoard> {
    return this.request(`/kanban/teams/${encodeURIComponent(team)}/default`);
  }

  /** 1.9 List Accessible Teams */
  async listTeams(): Promise<{ teams: string[] }> {
    const raw = await this.request<unknown>('/kanban/teams');
    // Backend may return a bare JSON array (e.g. `["event","management"]`) or `{ teams: [...] }`.
    const teamsPayload = firstArray(
      raw,
      raw && typeof raw === 'object' && raw !== null && 'teams' in raw
        ? (raw as { teams?: unknown }).teams
        : undefined,
    );
    return { teams: normalizeKanbanTeamsResponse(teamsPayload) };
  }

  /** 1.10 Search Boards */
  async searchBoards(params: { q: string; team?: string; page?: number; limit?: number }): Promise<{ results: KanbanBoard[] }> {
    const qs = this.buildQueryString(params);
    const raw = await this.request<unknown>(`/kanban/boards/search${qs}`);
    return coerceSearchBoardPayload(raw);
  }

  // ── 2. List Endpoints (6) ───────────────────────────────────────────────

  /** 2.1 Create List */
  async createList(boardId: string, data: CreateListRequest): Promise<KanbanList> {
    return this.request(`/kanban/boards/${encodeURIComponent(boardId)}/lists`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  /** 2.2 List Lists */
  async listLists(boardId: string): Promise<KanbanList[]> {
    return this.request(`/kanban/boards/${encodeURIComponent(boardId)}/lists`);
  }

  /** 2.3 Update List */
  async updateList(listId: string, data: UpdateListRequest): Promise<KanbanList> {
    return this.request(`/kanban/lists/${encodeURIComponent(listId)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  /** 2.4 Move List */
  async moveList(listId: string, position: number): Promise<KanbanList> {
    return this.request(`/kanban/lists/${encodeURIComponent(listId)}/move`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ position }),
    });
  }

  /** 2.5 Archive / Unarchive List */
  async archiveList(listId: string, isArchived: boolean): Promise<KanbanList> {
    return this.request(`/kanban/lists/${encodeURIComponent(listId)}/archive`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ is_archived: isArchived }),
    });
  }

  /** 2.6 Delete List */
  async deleteList(listId: string): Promise<{ ok: boolean; deleted: string }> {
    return this.request(`/kanban/lists/${encodeURIComponent(listId)}`, {
      method: 'DELETE',
    });
  }

  // ── 3. Swimlane Endpoints (7) ──────────────────────────────────────────

  /** 3.1 Create Swimlane */
  async createSwimlane(boardId: string, data: CreateSwimlaneRequest): Promise<KanbanSwimlane> {
    return this.request(`/kanban/boards/${encodeURIComponent(boardId)}/swimlanes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  /** 3.2 List Swimlanes */
  async listSwimlanes(boardId: string): Promise<KanbanSwimlane[]> {
    return this.request(`/kanban/boards/${encodeURIComponent(boardId)}/swimlanes`);
  }

  /** 3.3 Get Swimlane */
  async getSwimlane(swimlaneId: string): Promise<KanbanSwimlane> {
    return this.request(`/kanban/swimlanes/${encodeURIComponent(swimlaneId)}`);
  }

  /** 3.4 Update Swimlane */
  async updateSwimlane(swimlaneId: string, data: UpdateSwimlaneRequest): Promise<KanbanSwimlane> {
    return this.request(`/kanban/swimlanes/${encodeURIComponent(swimlaneId)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  /** 3.5 Move Swimlane */
  async moveSwimlane(swimlaneId: string, position: number): Promise<KanbanSwimlane> {
    return this.request(`/kanban/swimlanes/${encodeURIComponent(swimlaneId)}/move`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ position }),
    });
  }

  /** 3.6 Collapse / Expand Swimlane */
  async collapseSwimlane(swimlaneId: string, isCollapsed: boolean): Promise<KanbanSwimlane> {
    return this.request(`/kanban/swimlanes/${encodeURIComponent(swimlaneId)}/collapse`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ is_collapsed: isCollapsed }),
    });
  }

  /** 3.7 Delete Swimlane */
  async deleteSwimlane(swimlaneId: string): Promise<{ ok: boolean; deleted: string }> {
    return this.request(`/kanban/swimlanes/${encodeURIComponent(swimlaneId)}`, {
      method: 'DELETE',
    });
  }

  // ── 4. Card Endpoints (15) ─────────────────────────────────────────────

  /** 4.1 Create Card */
  async createCard(boardId: string, data: CreateCardRequest): Promise<KanbanCard> {
    return this.request(`/kanban/boards/${encodeURIComponent(boardId)}/cards`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  /** 4.2 List Cards */
  async listCards(boardId: string, params?: ListCardsParams): Promise<KanbanPaginatedResponse<KanbanCard>> {
    const qs = this.buildQueryString({
      list_id: params?.list_id,
      swimlane_id: params?.swimlane_id,
      priority_min: params?.priority_min,
      priority_max: params?.priority_max,
      card_type: params?.card_type,
      class_of_service: params?.class_of_service,
      is_blocked: params?.is_blocked,
      is_archived: params?.is_archived,
      search: params?.search,
      sort_by: params?.sort_by,
      sort_order: params?.sort_order,
      page: params?.page,
      limit: params?.limit,
    });
    const raw = await this.request<unknown>(`/kanban/boards/${encodeURIComponent(boardId)}/cards${qs}`);
    return coercePaginatedPayload<KanbanCard>(raw);
  }

  /**
   * GET /kanban/cards/:id — response may be a flat `KanbanCardFull` or wrapped
   * `{ card: KanbanCard, labels, … }` with plural `*_count` fields at the root.
   */
  async getCard(cardId: string): Promise<KanbanCardFull> {
    const raw = await this.request<unknown>(`/kanban/cards/${encodeURIComponent(cardId)}`);
    return coerceKanbanCardFullResponse(raw);
  }

  /** 4.4 Update Card */
  async updateCard(cardId: string, data: UpdateCardRequest): Promise<KanbanCard> {
    return this.request(`/kanban/cards/${encodeURIComponent(cardId)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  /** 4.5 Move Card */
  async moveCard(cardId: string, data: MoveCardRequest): Promise<KanbanCard> {
    return this.request(`/kanban/cards/${encodeURIComponent(cardId)}/move`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  /** 4.6 Archive / Unarchive Card */
  async archiveCard(cardId: string, isArchived: boolean): Promise<KanbanCard> {
    return this.request(`/kanban/cards/${encodeURIComponent(cardId)}/archive`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ is_archived: isArchived }),
    });
  }

  /** 4.7 Delete Card */
  async deleteCard(cardId: string): Promise<{ ok: boolean; deleted: string }> {
    return this.request(`/kanban/cards/${encodeURIComponent(cardId)}`, {
      method: 'DELETE',
    });
  }

  /** 4.8 Block / Unblock Card */
  async blockCard(cardId: string, data: BlockCardRequest): Promise<KanbanCard> {
    return this.request(`/kanban/cards/${encodeURIComponent(cardId)}/block`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  /** 4.9 Cards Assigned to User */
  async getCardsByAssignee(
    userUuid: string,
    params?: { team?: string; is_archived?: boolean; page?: number; limit?: number }
  ): Promise<KanbanPaginatedResponse<KanbanCard>> {
    const qs = this.buildQueryString({
      team: params?.team,
      is_archived: params?.is_archived,
      page: params?.page,
      limit: params?.limit,
    });
    const raw = await this.request<unknown>(`/kanban/cards/assigned/${encodeURIComponent(userUuid)}${qs}`);
    return coercePaginatedPayload<KanbanCard>(raw);
  }

  /** 4.10 Overdue Cards */
  async getOverdueCards(boardId: string, params?: { page?: number; limit?: number }): Promise<KanbanPaginatedResponse<KanbanCard>> {
    const qs = this.buildQueryString({ page: params?.page, limit: params?.limit });
    const raw = await this.request<unknown>(`/kanban/boards/${encodeURIComponent(boardId)}/cards/overdue${qs}`);
    return coercePaginatedPayload<KanbanCard>(raw);
  }

  /** 4.11 Search Cards */
  async searchCards(params: { q: string; team?: string; board_id?: string; page?: number; limit?: number }): Promise<{ results: KanbanCardSummary[] }> {
    const qs = this.buildQueryString(params);
    return this.request(`/kanban/cards/search${qs}`);
  }

  /** 4.12 Batch Move Cards */
  async batchMoveCards(boardId: string, data: BatchMoveRequest): Promise<{ ok: boolean; moved: number }> {
    return this.request(`/kanban/boards/${encodeURIComponent(boardId)}/cards/batch-move`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  /** 4.13 Cards by Due Date Range */
  async getCardsByDueRange(
    boardId: string,
    from: number,
    to: number,
    params?: { page?: number; limit?: number }
  ): Promise<KanbanPaginatedResponse<KanbanCard>> {
    const qs = this.buildQueryString({ from, to, page: params?.page, limit: params?.limit });
    const raw = await this.request<unknown>(`/kanban/boards/${encodeURIComponent(boardId)}/cards/due-range${qs}`);
    return coercePaginatedPayload<KanbanCard>(raw);
  }

  /** 4.14 Card Count by List */
  async getCardCountByList(boardId: string): Promise<CardCountByList[]> {
    return this.request(`/kanban/boards/${encodeURIComponent(boardId)}/cards/count-by-list`);
  }

  /** 4.15 Card History */
  async getCardHistory(cardId: string, params?: { page?: number; limit?: number }): Promise<KanbanCardHistory[]> {
    const qs = this.buildQueryString({ page: params?.page, limit: params?.limit });
    return this.request(`/kanban/cards/${encodeURIComponent(cardId)}/history${qs}`);
  }

  // ── 5. Assignee Endpoints (3) ──────────────────────────────────────────

  /** 5.1 Add Assignee */
  async addAssignee(cardId: string, userUuid: string): Promise<KanbanAssignee> {
    return this.request(`/kanban/cards/${encodeURIComponent(cardId)}/assignees`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_id: userUuid }),
    });
  }

  /** 5.2 Remove Assignee */
  async removeAssignee(cardId: string, userUuid: string): Promise<{ ok: boolean }> {
    return this.request(`/kanban/cards/${encodeURIComponent(cardId)}/assignees/${encodeURIComponent(userUuid)}`, {
      method: 'DELETE',
    });
  }

  /** 5.3 List Assignees */
  async listAssignees(cardId: string): Promise<KanbanAssignee[]> {
    return this.request(`/kanban/cards/${encodeURIComponent(cardId)}/assignees`);
  }

  // ── 6. Label Endpoints (8) ─────────────────────────────────────────────

  /** 6.1 Create Label */
  async createLabel(boardId: string, data: CreateLabelRequest): Promise<KanbanLabel> {
    return this.request(`/kanban/boards/${encodeURIComponent(boardId)}/labels`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  /** 6.2 List Labels */
  async listLabels(boardId: string): Promise<KanbanLabel[]> {
    return this.request(`/kanban/boards/${encodeURIComponent(boardId)}/labels`);
  }

  /** 6.3 Get Label */
  async getLabel(labelId: string): Promise<KanbanLabel> {
    return this.request(`/kanban/labels/${encodeURIComponent(labelId)}`);
  }

  /** 6.4 Update Label */
  async updateLabel(labelId: string, data: UpdateLabelRequest): Promise<KanbanLabel> {
    return this.request(`/kanban/labels/${encodeURIComponent(labelId)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  /** 6.5 Delete Label */
  async deleteLabel(labelId: string): Promise<{ ok: boolean; deleted: string }> {
    return this.request(`/kanban/labels/${encodeURIComponent(labelId)}`, {
      method: 'DELETE',
    });
  }

  /** 6.6 Add Label to Card */
  async addLabelToCard(cardId: string, labelId: string): Promise<{ ok: boolean }> {
    return this.request(`/kanban/cards/${encodeURIComponent(cardId)}/labels/${encodeURIComponent(labelId)}`, {
      method: 'POST',
    });
  }

  /** 6.7 Remove Label from Card */
  async removeLabelFromCard(cardId: string, labelId: string): Promise<{ ok: boolean }> {
    return this.request(`/kanban/cards/${encodeURIComponent(cardId)}/labels/${encodeURIComponent(labelId)}`, {
      method: 'DELETE',
    });
  }

  /** 6.8 List Labels for Card */
  async listCardLabels(cardId: string): Promise<KanbanLabel[]> {
    return this.request(`/kanban/cards/${encodeURIComponent(cardId)}/labels`);
  }

  // ── 7. Checklist Endpoints (11) ────────────────────────────────────────

  /** 7.1 Create Checklist */
  async createChecklist(cardId: string, data?: CreateChecklistRequest): Promise<KanbanChecklist> {
    return this.request(`/kanban/cards/${encodeURIComponent(cardId)}/checklists`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data || {}),
    });
  }

  /** 7.2 List Checklists for Card */
  async listChecklists(cardId: string): Promise<KanbanChecklist[]> {
    return this.request(`/kanban/cards/${encodeURIComponent(cardId)}/checklists`);
  }

  /** 7.3 Get Checklist */
  async getChecklist(checklistId: string): Promise<KanbanChecklist> {
    return this.request(`/kanban/checklists/${encodeURIComponent(checklistId)}`);
  }

  /** 7.4 Update Checklist */
  async updateChecklist(checklistId: string, data: UpdateChecklistRequest): Promise<KanbanChecklist> {
    return this.request(`/kanban/checklists/${encodeURIComponent(checklistId)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  /** 7.5 Move Checklist */
  async moveChecklist(checklistId: string, position: number): Promise<KanbanChecklist> {
    return this.request(`/kanban/checklists/${encodeURIComponent(checklistId)}/move`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ position }),
    });
  }

  /** 7.6 Delete Checklist */
  async deleteChecklist(checklistId: string): Promise<{ ok: boolean; deleted: string }> {
    return this.request(`/kanban/checklists/${encodeURIComponent(checklistId)}`, {
      method: 'DELETE',
    });
  }

  /** 7.7 Create Checklist Item */
  async createChecklistItem(checklistId: string, data: CreateChecklistItemRequest): Promise<KanbanChecklistItem> {
    return this.request(`/kanban/checklists/${encodeURIComponent(checklistId)}/items`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  /** 7.8 Update Checklist Item */
  async updateChecklistItem(itemId: string, data: UpdateChecklistItemRequest): Promise<KanbanChecklistItem> {
    return this.request(`/kanban/checklist-items/${encodeURIComponent(itemId)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  /** 7.9 Toggle Checklist Item */
  async toggleChecklistItem(itemId: string, isChecked: boolean): Promise<KanbanChecklistItem> {
    return this.request(`/kanban/checklist-items/${encodeURIComponent(itemId)}/toggle`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ is_checked: isChecked }),
    });
  }

  /** 7.10 Move Checklist Item */
  async moveChecklistItem(itemId: string, position: number): Promise<KanbanChecklistItem> {
    return this.request(`/kanban/checklist-items/${encodeURIComponent(itemId)}/move`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ position }),
    });
  }

  /** 7.11 Delete Checklist Item */
  async deleteChecklistItem(itemId: string): Promise<{ ok: boolean; deleted: string }> {
    return this.request(`/kanban/checklist-items/${encodeURIComponent(itemId)}`, {
      method: 'DELETE',
    });
  }

  // ── 8. Comment Endpoints (5) ───────────────────────────────────────────

  /** 8.1 Create Comment */
  async createComment(cardId: string, data: CreateCommentRequest): Promise<KanbanComment> {
    return this.request(`/kanban/cards/${encodeURIComponent(cardId)}/comments`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  /** 8.2 List Comments */
  async listComments(cardId: string, params?: { page?: number; limit?: number }): Promise<KanbanComment[]> {
    const qs = this.buildQueryString({ page: params?.page, limit: params?.limit });
    return this.request(`/kanban/cards/${encodeURIComponent(cardId)}/comments${qs}`);
  }

  /** 8.3 Get Comment */
  async getComment(commentId: string): Promise<KanbanComment> {
    return this.request(`/kanban/comments/${encodeURIComponent(commentId)}`);
  }

  /** 8.4 Update Comment */
  async updateComment(commentId: string, data: UpdateCommentRequest): Promise<KanbanComment> {
    return this.request(`/kanban/comments/${encodeURIComponent(commentId)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  /** 8.5 Delete Comment */
  async deleteComment(commentId: string): Promise<{ ok: boolean; deleted: string }> {
    return this.request(`/kanban/comments/${encodeURIComponent(commentId)}`, {
      method: 'DELETE',
    });
  }

  // ── 9. Attachment Endpoints (4) ────────────────────────────────────────

  /** 9.1 Upload Attachment(s) — multipart/form-data */
  async uploadAttachments(cardId: string, files: File[]): Promise<KanbanAttachment[]> {
    const formData = new FormData();
    files.forEach((file) => formData.append('file', file));

    const response = await fetch(`${KANBAN_API_BASE_URL}/kanban/cards/${encodeURIComponent(cardId)}/attachments`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`API Error: ${response.status} ${response.statusText} - ${errorText}`);
    }

    return response.json();
  }

  /** 9.2 List Attachments */
  async listAttachments(cardId: string): Promise<KanbanAttachment[]> {
    return this.request(`/kanban/cards/${encodeURIComponent(cardId)}/attachments`);
  }

  /** 9.3 Download Attachment — returns raw Response (binary) */
  async downloadAttachment(attachmentId: string): Promise<Response> {
    return this.requestRaw(`/kanban/attachments/${encodeURIComponent(attachmentId)}/download`);
  }

  /** 9.4 Delete Attachment */
  async deleteAttachment(attachmentId: string): Promise<{ ok: boolean; deleted: string }> {
    return this.request(`/kanban/attachments/${encodeURIComponent(attachmentId)}`, {
      method: 'DELETE',
    });
  }

  // ── 10. Watcher Endpoints (3) ──────────────────────────────────────────

  /** 10.1 Watch Card */
  async watchCard(cardId: string): Promise<KanbanWatcher> {
    return this.request(`/kanban/cards/${encodeURIComponent(cardId)}/watchers`, {
      method: 'POST',
    });
  }

  /** 10.2 Unwatch Card */
  async unwatchCard(cardId: string): Promise<{ ok: boolean }> {
    return this.request(`/kanban/cards/${encodeURIComponent(cardId)}/watchers`, {
      method: 'DELETE',
    });
  }

  /** 10.3 List Watchers */
  async listWatchers(cardId: string): Promise<KanbanWatcher[]> {
    return this.request(`/kanban/cards/${encodeURIComponent(cardId)}/watchers`);
  }

  // ── 11. Card Link Endpoints (4) ────────────────────────────────────────

  /** 11.1 Create Card Link */
  async createCardLink(cardId: string, data: CreateCardLinkRequest): Promise<KanbanCardLink> {
    return this.request(`/kanban/cards/${encodeURIComponent(cardId)}/links`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  /** 11.2 List Card Links */
  async listCardLinks(cardId: string): Promise<KanbanCardLink[]> {
    return this.request(`/kanban/cards/${encodeURIComponent(cardId)}/links`);
  }

  /** 11.3 Delete Card Link */
  async deleteCardLink(linkId: string): Promise<{ ok: boolean; deleted: string }> {
    return this.request(`/kanban/card-links/${encodeURIComponent(linkId)}`, {
      method: 'DELETE',
    });
  }

  /** 11.4 Reverse Link Lookup */
  async reverseCardLinkLookup(linkType: string, targetId: string): Promise<KanbanCardLink[]> {
    const qs = this.buildQueryString({ link_type: linkType, link_target_id: targetId });
    return this.request(`/kanban/card-links/reverse${qs}`);
  }

  // ── 12. Cadence Endpoints (6) ──────────────────────────────────────────

  /** 12.1 Create Cadence */
  async createCadence(boardId: string, data: CreateCadenceRequest): Promise<KanbanCadence> {
    return this.request(`/kanban/boards/${encodeURIComponent(boardId)}/cadences`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  /** 12.2 List Cadences */
  async listCadences(boardId: string): Promise<KanbanCadence[]> {
    return this.request(`/kanban/boards/${encodeURIComponent(boardId)}/cadences`);
  }

  /** 12.3 Get Cadence */
  async getCadence(cadenceId: string): Promise<KanbanCadence> {
    return this.request(`/kanban/cadences/${encodeURIComponent(cadenceId)}`);
  }

  /** 12.4 Update Cadence */
  async updateCadence(cadenceId: string, data: UpdateCadenceRequest): Promise<KanbanCadence> {
    return this.request(`/kanban/cadences/${encodeURIComponent(cadenceId)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  /** 12.5 Delete Cadence */
  async deleteCadence(cadenceId: string): Promise<{ ok: boolean; deleted: string }> {
    return this.request(`/kanban/cadences/${encodeURIComponent(cadenceId)}`, {
      method: 'DELETE',
    });
  }

  /** 12.6 Upcoming Cadences */
  async getUpcomingCadences(params?: { team?: string; limit?: number }): Promise<KanbanUpcomingCadence[]> {
    const qs = this.buildQueryString({ team: params?.team, limit: params?.limit });
    return this.request(`/kanban/cadences/upcoming${qs}`);
  }

  // ── 13. Board Settings Endpoints (3) ───────────────────────────────────

  /** 13.1 Set Board Setting */
  async setBoardSetting(boardId: string, key: string, data: SetBoardSettingRequest): Promise<KanbanBoardSetting> {
    return this.request(`/kanban/boards/${encodeURIComponent(boardId)}/settings/${encodeURIComponent(key)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  /** 13.2 Get Board Setting */
  async getBoardSetting(boardId: string, key: string): Promise<KanbanBoardSetting> {
    return this.request(`/kanban/boards/${encodeURIComponent(boardId)}/settings/${encodeURIComponent(key)}`);
  }

  /** 13.3 List All Board Settings */
  async listBoardSettings(boardId: string): Promise<KanbanBoardSetting[]> {
    const raw = await this.request<unknown>(`/kanban/boards/${encodeURIComponent(boardId)}/settings`);
    return coerceKanbanBoardSettings(raw);
  }

  // ── 14. Metrics Endpoints (7) ──────────────────────────────────────────

  /** 14.1 Lead Time */
  async getLeadTime(boardId: string, from: number, to: number): Promise<KanbanLeadCycleTimeMetrics> {
    const qs = this.buildQueryString({ from, to });
    return this.request(`/kanban/boards/${encodeURIComponent(boardId)}/metrics/lead-time${qs}`);
  }

  /** 14.2 Cycle Time */
  async getCycleTime(boardId: string, from: number, to: number): Promise<KanbanLeadCycleTimeMetrics> {
    const qs = this.buildQueryString({ from, to });
    return this.request(`/kanban/boards/${encodeURIComponent(boardId)}/metrics/cycle-time${qs}`);
  }

  /** 14.3 Throughput */
  async getThroughput(boardId: string, from: number, to: number, granularity?: 'day' | 'week' | 'month'): Promise<KanbanThroughputMetrics> {
    const qs = this.buildQueryString({ from, to, granularity });
    return this.request(`/kanban/boards/${encodeURIComponent(boardId)}/metrics/throughput${qs}`);
  }

  /** 14.4 WIP Aging */
  async getWIPAging(boardId: string): Promise<KanbanWIPAgingEntry[]> {
    return this.request(`/kanban/boards/${encodeURIComponent(boardId)}/metrics/wip-aging`);
  }

  /** 14.5 Bottlenecks */
  async getBottlenecks(boardId: string): Promise<KanbanBottleneckEntry[]> {
    return this.request(`/kanban/boards/${encodeURIComponent(boardId)}/metrics/bottlenecks`);
  }

  /** 14.6 CFD Data */
  async getCFD(boardId: string, from?: string, to?: string): Promise<KanbanCFDEntry[]> {
    const qs = this.buildQueryString({ from, to });
    return this.request(`/kanban/boards/${encodeURIComponent(boardId)}/metrics/cfd${qs}`);
  }

  /** 14.7 Metrics Summary */
  async getMetricsSummary(boardId: string, from?: string, to?: string): Promise<KanbanMetricsSummary> {
    const qs = this.buildQueryString({ from, to });
    return this.request(`/kanban/boards/${encodeURIComponent(boardId)}/metrics/summary${qs}`);
  }

  // ── 15. Activity Endpoints (4) ─────────────────────────────────────────

  /** 15.1 Board Activity Log */
  async getBoardActivity(boardId: string, params?: { page?: number; limit?: number }): Promise<KanbanPaginatedResponse<KanbanActivityLogEntry>> {
    const qs = this.buildQueryString({ page: params?.page, limit: params?.limit });
    const raw = await this.request<unknown>(`/kanban/boards/${encodeURIComponent(boardId)}/activity${qs}`);
    return coercePaginatedPayload<KanbanActivityLogEntry>(raw);
  }

  /** 15.2 Card Activity Log */
  async getCardActivity(cardId: string, params?: { page?: number; limit?: number }): Promise<KanbanPaginatedResponse<KanbanActivityLogEntry>> {
    const qs = this.buildQueryString({ page: params?.page, limit: params?.limit });
    const raw = await this.request<unknown>(`/kanban/cards/${encodeURIComponent(cardId)}/activity${qs}`);
    return coercePaginatedPayload<KanbanActivityLogEntry>(raw);
  }

  /** 15.3 Recent Activity (cross-board) */
  async getRecentActivity(params?: { team?: string; limit?: number }): Promise<KanbanActivityLogEntry[]> {
    const qs = this.buildQueryString({ team: params?.team, limit: params?.limit });
    const raw = await this.request<unknown>(`/kanban/activity/recent${qs}`);
    return coerceRecentActivityPayload(raw);
  }

  /** 15.4 Activity by Actor */
  async getActivityByActor(actorId: string, params?: { page?: number; limit?: number }): Promise<KanbanPaginatedResponse<KanbanActivityLogEntry>> {
    const qs = this.buildQueryString({ page: params?.page, limit: params?.limit });
    const raw = await this.request<unknown>(`/kanban/activity/by-actor/${encodeURIComponent(actorId)}${qs}`);
    return coercePaginatedPayload<KanbanActivityLogEntry>(raw);
  }

  // ── 16. Stats Endpoints (2) ────────────────────────────────────────────

  /** 16.1 Team-wide Stats */
  async getTeamStats(params?: { team?: string }): Promise<KanbanStats> {
    const qs = this.buildQueryString({ team: params?.team });
    return this.request(`/kanban/stats${qs}`);
  }

  /** 16.2 Board Stats */
  async getBoardStats(boardId: string): Promise<KanbanStats> {
    return this.request(`/kanban/boards/${encodeURIComponent(boardId)}/stats`);
  }

  // ── 17. Incident bridge reverse lookups ────────────────────────────────

  /**
   * `GET /kanban/cards/{cardId}/incidents` — incidents bridged to this card.
   *
   * Distinct from `reverseCardLinkLookup('incident', incidentId)`, which lists kanban cards
   * shallow-linked to a given incident via the legacy `kanban_card_links` table. The
   * dedicated endpoint here returns full `Incident` rows.
   */
  async getIncidentsForCard(cardId: string): Promise<Incident[]> {
    return this.request<Incident[]>(`/kanban/cards/${encodeURIComponent(cardId)}/incidents`);
  }

  /** `GET /kanban/boards/{boardId}/incidents` — any incident linked to any card on this board. */
  async getIncidentsForBoard(boardId: string): Promise<Incident[]> {
    return this.request<Incident[]>(`/kanban/boards/${encodeURIComponent(boardId)}/incidents`);
  }

  // ── 18. Sync-aware kanban companions (`*-with-sync`) ───────────────────

  /**
   * `POST /kanban/cards/{cardId}/move-with-sync` — move a card AND propagate to incidents
   * with `sync_status` per the board's status-map. Body identical to legacy `moveCard`.
   *
   * NOTE: backend route is `POST` (not `PATCH`) per the integration spec.
   */
  async moveCardWithSync(cardId: string, data: MoveCardRequest): Promise<KanbanCard> {
    return this.request<KanbanCard>(`/kanban/cards/${encodeURIComponent(cardId)}/move-with-sync`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  /** `PATCH /kanban/cards/{cardId}/with-sync` — patch fields AND propagate priority if `sync_priority`. */
  async updateCardWithSync(cardId: string, data: UpdateCardRequest): Promise<KanbanCard> {
    return this.request<KanbanCard>(`/kanban/cards/${encodeURIComponent(cardId)}/with-sync`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  /** `POST /kanban/cards/{cardId}/assignees/with-sync` — add assignee AND propagate if `sync_assignees`. */
  async addAssigneeWithSync(cardId: string, userUuid: string): Promise<KanbanAssignee> {
    return this.request<KanbanAssignee>(
      `/kanban/cards/${encodeURIComponent(cardId)}/assignees/with-sync`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userUuid }),
      },
    );
  }

  /** `DELETE /kanban/cards/{cardId}/assignees/{userUuid}/with-sync`. */
  async removeAssigneeWithSync(cardId: string, userUuid: string): Promise<{ ok: boolean }> {
    return this.request<{ ok: boolean }>(
      `/kanban/cards/${encodeURIComponent(cardId)}/assignees/${encodeURIComponent(userUuid)}/with-sync`,
      { method: 'DELETE' },
    );
  }

  /** `POST /kanban/cards/{cardId}/comments/with-sync` — add comment AND mirror if `sync_comments`. */
  async addCommentWithSync(cardId: string, data: CreateCommentRequest): Promise<KanbanComment> {
    return this.request<KanbanComment>(
      `/kanban/cards/${encodeURIComponent(cardId)}/comments/with-sync`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      },
    );
  }
}

// Export as singleton
export const kanbanApiService = new KanbanApiService();
